import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { services } from '@/data/siteData';
import { 
  Monitor, 
  Code, 
  Cloud, 
  Shield,
  ArrowRight,
  CheckCircle2,
  Clock,
  ChevronDown,
  Phone
} from 'lucide-react';

const iconMap = { Monitor, Code, Cloud, Shield };

// Content Renderer - Properly formats paragraphs and bullet points
const ContentRenderer = ({ content }) => {
  if (!content) return null;
  
  // Split by double newlines to get sections
  const sections = content.split('\n\n');
  
  return (
    <div className="space-y-6">
      {sections.map((section, sectionIndex) => {
        // Check if this section contains bullet points
        const lines = section.split('\n');
        const hasBullets = lines.some(line => line.trim().startsWith('•'));
        
        if (hasBullets) {
          // Separate heading from bullet points
          const headingLines = [];
          const bulletLines = [];
          
          lines.forEach(line => {
            if (line.trim().startsWith('•')) {
              bulletLines.push(line.trim().substring(1).trim());
            } else if (line.trim()) {
              headingLines.push(line.trim());
            }
          });
          
          return (
            <div key={sectionIndex} className="space-y-4">
              {headingLines.map((heading, hIndex) => (
                <h3 key={hIndex} className="text-lg font-semibold text-gray-900">{heading}</h3>
              ))}
              <ul className="space-y-3">
                {bulletLines.map((bullet, bIndex) => (
                  <li key={bIndex} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#0066CC] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        } else {
          // Check if it's a heading (short text ending with colon or question mark)
          const isHeading = section.length < 100 && (section.endsWith(':') || section.endsWith('?'));
          
          if (isHeading) {
            return (
              <h3 key={sectionIndex} className="text-lg font-semibold text-gray-900">
                {section}
              </h3>
            );
          }
          
          return (
            <p key={sectionIndex} className="text-gray-600 leading-relaxed">
              {section}
            </p>
          );
        }
      })}
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ faq, isOpen, toggle, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.05 }}
    className="border-b border-gray-200"
  >
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between py-5 text-left group"
    >
      <span className="font-medium text-gray-900 group-hover:text-[#0066CC] transition-colors pr-8">
        {faq.question}
      </span>
      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-5 text-gray-600 leading-relaxed">
        {faq.answer}
      </p>
    </motion.div>
  </motion.div>
);

// Stats Grid Component
const StatsGrid = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 py-12 border-y border-gray-200">
    {stats.map((stat, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="text-center"
      >
        <p className="text-3xl lg:text-4xl font-bold text-[#0066CC] mb-1">{stat.value}</p>
        <p className="text-sm text-gray-600">{stat.label}</p>
      </motion.div>
    ))}
  </div>
);

export const ServiceDetailPage = () => {
  const { serviceId, subServiceId } = useParams();
  const [service, setService] = useState(null);
  const [activeSubService, setActiveSubService] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const foundService = services[serviceId];
    if (foundService) {
      setService(foundService);
      if (subServiceId) {
        setActiveSubService(foundService.subServices.find(s => s.id === subServiceId));
      } else {
        setActiveSubService(null);
      }
    }
  }, [serviceId, subServiceId]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Monitor;

  // Sub-service detail view
  if (activeSubService) {
    const subFaqs = activeSubService.faqs || [];
    const subStats = activeSubService.stats || [];

    return (
      <>
        <Helmet>
          <title>{activeSubService.title} - {service.title} | Felix Infotech</title>
          <meta name="description" content={activeSubService.description} />
        </Helmet>

        <ScrollProgress />
        <Navbar />

        <main>
          <AnimatedPageHero
            title={activeSubService.title}
            description={activeSubService.description}
            badge={service.title}
            breadcrumbs={[
              { label: 'Services', href: '/services' },
              { label: service.title, href: `/services/${service.id}` },
              { label: activeSubService.title }
            ]}
          >
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-[#0066CC] text-white font-medium rounded-full hover:bg-[#0055AA] transition-colors">
              Get Started <ArrowRight className="w-4 h-4 ml-2 text-white" />
            </Link>
          </AnimatedPageHero>

          {/* Stats */}
          {subStats.length > 0 && (
            <section className="container mx-auto px-6">
              <StatsGrid stats={subStats} />
            </section>
          )}

          {/* Main Content */}
          <section className="py-16">
            <div className="container mx-auto px-6">
              <div className="grid lg:grid-cols-3 gap-12">
                {/* Content */}
                <div className="lg:col-span-2 space-y-12">
                  {/* Overview - Using ContentRenderer for proper formatting */}
                  {activeSubService.longDescription && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Overview</h2>
                      <ContentRenderer content={activeSubService.longDescription} />
                    </motion.div>
                  )}

                  {/* Features */}
                  {activeSubService.features && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {activeSubService.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                            <CheckCircle2 className="w-5 h-5 text-[#0066CC] flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Benefits */}
                  {activeSubService.benefits && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits</h2>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {activeSubService.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-center gap-3 p-4 bg-[#0066CC]/5 rounded-xl">
                            <CheckCircle2 className="w-5 h-5 text-[#0066CC] flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Delivery */}
                  {(activeSubService.deliveryTime || activeSubService.deliverables) && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 rounded-2xl p-8"
                    >
                      <h2 className="text-xl font-bold text-gray-900 mb-6">Service Delivery</h2>
                      <div className="grid sm:grid-cols-2 gap-8">
                        {activeSubService.deliveryTime && (
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
                              <Clock className="w-6 h-6 text-[#0066CC]" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Timeline</p>
                              <p className="font-semibold text-lg text-gray-900">{activeSubService.deliveryTime}</p>
                            </div>
                          </div>
                        )}
                        {activeSubService.deliverables && (
                          <div>
                            <p className="text-sm text-gray-500 mb-3">Deliverables</p>
                            <div className="space-y-2">
                              {activeSubService.deliverables.slice(0, 5).map((item, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                                  <CheckCircle2 className="w-4 h-4 text-[#0066CC]" />
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* FAQs */}
                  {subFaqs.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                      <div>
                        {subFaqs.map((faq, index) => (
                          <FAQItem
                            key={index}
                            faq={faq}
                            index={index}
                            isOpen={openFAQ === index}
                            toggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Sidebar - WHITE text on blue background */}
                <div className="space-y-6">
                  <div className="bg-[#0066CC] rounded-2xl p-6 sticky top-24">
                    <h3 style={{ color: 'white' }} className="text-xl font-bold mb-4">Need This Service?</h3>
                    <p style={{ color: 'white' }} className="mb-6 text-sm">
                      Contact us for a free consultation and customized quote.
                    </p>
                    <Link to="/contact" className="flex items-center justify-center px-6 py-3 bg-white text-[#0066CC] font-medium rounded-full hover:bg-gray-100 transition-colors mb-3">
                      Get Free Quote
                    </Link>
                    <a href="tel:044 42613839" className="flex items-center justify-center px-6 py-3 border border-white/30 rounded-full hover:bg-white/10 transition-colors">
                      <Phone className="w-4 h-4 mr-2 text-white" /> 
                      <span style={{ color: 'white' }}>Call Us</span>
                    </a>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Other {service.title}</h3>
                    <div className="space-y-2">
                      {service.subServices.filter(s => s.id !== activeSubService.id).slice(0, 5).map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/services/${service.id}/${sub.id}`}
                          className="flex items-center gap-2 p-3 rounded-lg hover:bg-white transition-colors text-sm text-gray-700"
                        >
                          <ArrowRight className="w-4 h-4 text-[#0066CC]" />
                          {sub.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <ContactForm />
        </main>

        <Footer />
        <ScrollToTop />
      </>
    );
  }

  // Main service page
  const mainFaqs = service.faqs || [];
  const mainStats = service.stats || [];

  return (
    <>
      <Helmet>
        <title>{service.title} - Felix Infotech | Enterprise Solutions</title>
        <meta name="description" content={service.description} />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title={service.title}
          description={service.description}
          badge="Our Services"
          breadcrumbs={[
            { label: 'Services', href: '/services' },
            { label: service.title }
          ]}
        >
          <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-[#0066CC] text-white font-medium rounded-full hover:bg-[#0055AA] transition-colors">
            Get Started <ArrowRight className="w-4 h-4 ml-2 text-white" />
          </Link>
        </AnimatedPageHero>

        {/* Stats */}
        {mainStats.length > 0 && (
          <section className="container mx-auto px-6">
            <StatsGrid stats={mainStats} />
          </section>
        )}

        {/* Sub Services Grid */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Our {service.title} Offerings</h2>
                  <p className="text-gray-600 leading-relaxed mb-8">
                    Explore our comprehensive range of {service.title.toLowerCase()} designed to meet the evolving needs of modern enterprises. 
                    Each solution is backed by industry-leading expertise, cutting-edge technology, and dedicated support.
                  </p>
                </motion.div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {service.subServices.map((sub, index) => (
                    <motion.div
                      key={sub.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={`/services/${service.id}/${sub.id}`}
                        className="block p-6 bg-white rounded-xl border border-gray-100 hover:shadow-lg hover:border-[#0066CC]/30 transition-all group"
                      >
                        <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#0066CC] transition-colors">
                          {sub.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {sub.description}
                        </p>
                        <span className="inline-flex items-center text-sm text-[#0066CC] font-medium">
                          Learn more <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* FAQs */}
                {mainFaqs.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-16"
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                    <div>
                      {mainFaqs.map((faq, index) => (
                        <FAQItem
                          key={index}
                          faq={faq}
                          index={index}
                          isOpen={openFAQ === index}
                          toggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Sidebar - WHITE text on blue background */}
              <div className="space-y-6">
                <div className="bg-[#0066CC] rounded-2xl p-6 sticky top-24">
                  <h3 style={{ color: 'white' }} className="text-xl font-bold mb-4">Need This Service?</h3>
                  <p style={{ color: 'white' }} className="mb-6 text-sm">
                    Contact us for a free consultation and customized quote.
                  </p>
                  <Link to="/contact" className="flex items-center justify-center px-6 py-3 bg-white text-[#0066CC] font-medium rounded-full hover:bg-gray-100 transition-colors mb-3">
                    Get Free Quote
                  </Link>
                  <a href="tel:044 42613839" className="flex items-center justify-center px-6 py-3 border border-white/30 rounded-full hover:bg-white/10 transition-colors">
                    <Phone className="w-4 h-4 mr-2 text-white" /> 
                    <span style={{ color: 'white' }}>Call Us</span>
                  </a>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">All Services</h3>
                  <div className="space-y-2">
                    {Object.values(services).map((s) => (
                      <Link
                        key={s.id}
                        to={`/services/${s.id}`}
                        className={`flex items-center gap-2 p-3 rounded-lg transition-colors text-sm ${
                          s.id === service.id ? 'bg-[#0066CC] text-white' : 'hover:bg-white text-gray-700'
                        }`}
                      >
                        <ArrowRight className={`w-4 h-4 ${s.id === service.id ? 'text-white' : 'text-[#0066CC]'}`} />
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default ServiceDetailPage;
